/**
 * Updates: what the fleet runs, which releases this platform knows, and (Enterprise) which ring
 * is offered what (docs/plans/2026-10-update-management.md, packages/shared/src/schemas/releases.ts).
 * Cloud follows the portal as published and this page is a window on it; an Enterprise AIOE
 * follows Cloud and decides here. On Cloud the page is about the customer's own machines (AI Workbench and nodes):
 * the service's own release and the mesh agent are Mojo Up's to run, so neither is shown there. The order is the question a person asks: what happens next (the
 * rings by products grid, where approving happens), then how each product's releases reach the
 * rings, then which installers this platform keeps for machines that cannot reach Mojo Up.
 *
 * The data column (Layout.tsx): a header, a How it works, the numbers, then list groups: the rings
 * grid, one group per product (how it reaches the rings as rows, what runs, then a row per release
 * with its one control), and where machines download from.
 */

import { compareVersions, type ReleaseChannel, type ReleaseManifest, type ReleaseProduct, type ReleaseProductView, type ReleasesOverview } from '@aioe/shared';
import { dateMedium } from '@aioe/shared/wording/dateTime';
import { ArrowDownToLine, ArrowsRotateRight, Box, CircleCheck, Cpu, Display, HardDrive, Layers, Server } from '@gravity-ui/icons';
import { Button, Tooltip } from '@heroui/react';
import { Segment } from '@heroui-pro/react';
import { useMemo, useState, type ReactNode } from 'react';

import { problemText, routes } from '../api/client.ts';
import { Empty, ErrorNote, ListBlock, ListGroup, ListRow, PageColumn, PageHeader, Stat, StatusMark, Toggle } from '../components/ui.tsx';
import { VIEW_TONES } from '../components/ui/Art.tsx';
import { Explainer } from '../components/ui/Layout.tsx';
import { isCloud } from '../edition.ts';
import { timeAgo, useLoad } from '../hooks.ts';
import { Markdown } from '../components/Markdown.tsx';
import { WholeNumber } from '../components/updatesFields.tsx';
import { waitingApprovals, WhatHappensNext } from '../components/updatesNext.tsx';
import { patchProducts } from '../components/updatesPolicy.ts';
import { InstallersKept } from '../components/updatesStorage.tsx';
import { hasInstallerStorage } from '../components/updatesPolicy.ts';
import { PartBoundary } from '../components/ui/PartBoundary.tsx';

const tone = VIEW_TONES.updates!;

const PRODUCT: Record<ReleaseProduct, { title: string; what: string; icon: ReactNode }> = {
  workbench: { title: 'AI Workbench', what: 'The desktop app on each person’s computer', icon: <Display /> },
  node: { title: 'Nodes', what: 'Headless workbenches for shared compute', icon: <Cpu /> },
  mesh: { title: 'Mesh agent', what: 'The overlay agent each machine bundles', icon: <Layers /> },
  aioe: { title: 'AIOE platform', what: 'This service: its images and chart', icon: <Server /> },
};

const day = (iso: string) => dateMedium(iso);

/** Release notes are short Markdown. */
function Notes({ text }: { text: string }) {
  return <Markdown compact className="text-xs text-muted" text={text} />;
}

/** "Release notes" in a row's description: opens the notes under the row, or follows the release's page. */
function NotesLink({ release: r, open, onToggle }: { release: ReleaseManifest; open: boolean; onToggle: () => void }) {
  if (r.releaseNotes) {
    return (
      <button type="button" className="text-accent hover:underline" aria-expanded={open} onClick={onToggle}>
        {open ? 'Hide notes' : 'Release notes'}
      </button>
    );
  }
  return r.page ? (
    <a className="text-accent hover:underline" href={r.page} target="_blank" rel="noreferrer">
      Release notes
    </a>
  ) : null;
}

/** Devices on the newest version offered to anyone, and devices that have something newer waiting. */
function adoption(p: ReleaseProductView): { current: number; behind: number } {
  const offered = Object.values(p.offered);
  const newest = offered.length ? offered.reduce((a, b) => (compareVersions(a, b) >= 0 ? a : b)) : p.latest;
  if (!newest) return { current: p.running.reduce((n, r) => n + r.devices, 0), behind: 0 };
  let current = 0;
  let behind = 0;
  for (const r of p.running) {
    if (compareVersions(r.version, newest) >= 0) current += r.devices;
    else behind += r.devices;
  }
  return { current, behind };
}

export function UpdatesPage() {
  const view = useLoad(() => routes.releases(), [], 30_000);
  // What each machine runs, so the grid can say "Running 0.60.2 (12)" per ring. Enterprise only.
  const devices = useLoad(() => (isCloud() ? Promise.resolve(undefined) : routes.devices()), [], 60_000);
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const act = async (f: () => Promise<unknown>) => {
    setError(undefined);
    setBusy(true);
    try {
      await f();
      view.reload();
    } catch (e) {
      setError(problemText(e));
    } finally {
      setBusy(false);
    }
  };
  const v = view.data;
  const totals = useMemo(() => {
    const fleet = v ? v.products.filter((p) => p.product !== 'aioe' && (!isCloud() || p.product !== 'mesh')).map(adoption) : [];
    // A release waits for you only in approval mode: a newer release than a ring with machines is offered.
    const awaiting = v && !isCloud() ? waitingApprovals(v) : 0;
    return { current: fleet.reduce((n, a) => n + a.current, 0), behind: fleet.reduce((n, a) => n + a.behind, 0), awaiting, known: v ? v.products.reduce((n, p) => n + p.releases.length, 0) : undefined };
  }, [v]);
  const enterprise = !isCloud();
  // Cloud: only what the customer's machines run. The service and the mesh agent are Mojo Up's.
  const products = v ? (enterprise ? v.products : v.products.filter((p) => p.product === 'workbench' || p.product === 'node')) : undefined;
  const machines = products?.reduce((n, p) => n + p.running.reduce((m, r) => m + r.devices, 0), 0);
  const platformNewer = v?.platform.latest && compareVersions(v.platform.latest.version, v.platform.version) > 0 ? v.platform.latest : undefined;

  return (
    <PageColumn width="data">
      <PageHeader
        tone={tone}
        icon={<ArrowsRotateRight />}
        title="Updates"
        subtitle={enterprise ? 'Releases arrive from Mojo Up, and you decide which ring of machines gets each one.' : 'Releases of AI Workbench and nodes, and what each of your machines runs.'}
        actions={
          v?.upstream && enterprise ? (
            <Button size="sm" variant="secondary" isDisabled={busy} onPress={() => act(() => routes.syncReleases())}>
              Check now
            </Button>
          ) : undefined
        }
      />
      {enterprise ? (
        <Explainer id="updates" tone={tone} icon={<ArrowsRotateRight />} title="How updates work" summary="Mojo Up publishes releases; you decide which ring of machines gets each one">
          <p>This platform checks Mojo Up for new releases of AI Workbench, nodes and the mesh agent. Every machine belongs to one ring: the first ring it matches, or the last ring, which takes everyone else.</p>
          <p>For each product, a new release reaches the rings on its own after the wait you set, or only when you approve it for a ring under What happens next. A machine installs what its ring is offered on its own, within a day; Install now or roll back in a ring’s menu tells its machines at once.</p>
          <p>Machines download their updates from this platform when it has storage for them, so a closed network can update too.</p>
        </Explainer>
      ) : null}
      <PartBoundary key="updates" name="Updates" tone={tone}>
        <div className="art-stats">
          <Stat tone={tone} icon={<CircleCheck />} label="Up to date" value={v ? totals.current : undefined} detail={v ? 'On their offered version' : ''} />
          <Stat
            tone={tone}
            icon={<ArrowsRotateRight />}
            label="Updating"
            value={v ? totals.behind : undefined}
            detail={v ? (totals.awaiting ? `${totals.awaiting} ${totals.awaiting === 1 ? 'release waits' : 'releases wait'} for your approval` : totals.behind ? 'Install on their own within a day' : 'Nothing to install') : ''}
            needsYou={totals.awaiting > 0}
          />
          <Stat tone={tone} icon={<Box />} label="Releases" value={totals.known} detail={enterprise ? (v?.lastSyncAt ? `Checked ${timeAgo(v.lastSyncAt)}` : v?.upstream ? 'Not checked yet' : 'No upstream configured') : v ? 'Of AI Workbench and nodes' : ''} />
          {enterprise ? (
            <Stat tone={tone} icon={<Server />} label="This platform" value={v ? v.platform.version : undefined} detail={platformNewer ? `${platformNewer.version} is available` : v?.platform.latest ? 'The newest release' : ''} needsYou={!!platformNewer && !totals.awaiting} />
          ) : (
            <Stat tone={tone} icon={<Display />} label="Machines" value={machines} detail={v ? 'Workbenches and nodes reporting' : ''} />
          )}
        </div>
        <ErrorNote error={error ?? view.error ?? (enterprise ? v?.lastSyncError : undefined)} />

        {v && !v.upstream && enterprise ? (
          <Empty tone={tone} icon={<ArrowsRotateRight />} title="Not following Mojo Up’s releases yet" body="Whoever runs this deployment sets the address of Mojo Up’s releases (see Updating AIOE in the documentation). Then the fleet’s updates are managed from here." />
        ) : null}

        {v && enterprise ? <WhatHappensNext view={v} devices={devices.data} busy={busy} act={act} /> : null}

        {v && !enterprise && products?.every((p) => p.releases.length === 0) ? (
          <Empty tone={tone} icon={<ArrowsRotateRight />} title="No releases listed yet" body="New releases of AI Workbench and nodes appear here, with what each of your machines runs." />
        ) : null}

        {v && products?.map((p) => <ProductGroup key={p.product} product={p} view={v} enterprise={enterprise} busy={busy} act={act} />)}

        {v?.upstream && enterprise ? <InstallersKept view={v} busy={busy} act={act} /> : null}
      </PartBoundary>
    </PageColumn>
  );
}

/** The channels a product's releases reach rings on: each ring's own, else the product's. */
function channelsInUse(p: ReleaseProductView, view: ReleasesOverview): Set<ReleaseChannel> {
  return new Set(view.rings.map((r) => r.channel ?? p.policy.channel));
}

/** One product: how its releases reach the rings, what the fleet runs, and its releases, newest first. */
function ProductGroup({ product: p, view, enterprise, busy, act }: { product: ReleaseProductView; view: ReleasesOverview; enterprise: boolean; busy: boolean; act: (f: () => Promise<unknown>) => Promise<void> }) {
  const meta = PRODUCT[p.product];
  const total = p.running.reduce((n, r) => n + r.devices, 0);
  const [showAll, setShowAll] = useState(false);
  const isPlatform = p.product === 'aioe';
  const channels = isPlatform || !enterprise ? new Set<ReleaseChannel>([p.policy.channel]) : channelsInUse(p, view);
  const releases = p.releases.filter((r) => channels.has(r.channel) || r.yanked);
  const shown = showAll ? releases : releases.slice(0, 4);
  const setProduct = (patch: Partial<ReleaseProductView['policy']>) => act(() => patchProducts(view, [p.product as ReleaseProduct], patch));

  if (isPlatform) return <PlatformGroup view={view} />;
  return (
    <ListGroup
      title={meta.title}
      count={total || undefined}
      description={meta.what}
      actions={
        enterprise ? (
          <Segment aria-label={`How ${meta.title} releases reach the rings`} size="sm" selectedKey={p.policy.mode} onSelectionChange={(k) => void setProduct({ mode: k as 'follow' | 'approve' })} isDisabled={busy}>
            <Segment.Item id="follow">Automatically</Segment.Item>
            <Segment.Item id="approve">When I approve</Segment.Item>
          </Segment>
        ) : undefined
      }
    >
      {enterprise ? (
        p.policy.mode === 'follow' ? (
          <ListRow
            title="Days to wait"
            description={`New releases reach the rings on their own${p.policy.deferDays === 0 ? ', as soon as Mojo Up publishes them' : `, ${p.policy.deferDays} ${p.policy.deferDays === 1 ? 'day' : 'days'} after Mojo Up publishes them; a security or reliability fix arrives at once`}.`}
            control={<WholeNumber value={p.policy.deferDays} min={0} max={90} isDisabled={busy} ariaLabel="Days to wait after Mojo Up publishes a release" onChange={(deferDays) => void setProduct({ deferDays })} />}
          />
        ) : (
          <ListRow title="Approve each release" description="A new release reaches a ring only when you approve it for that ring under What happens next." />
        )
      ) : null}
      {enterprise ? (
        <ListRow
          title="Install urgent fixes when this platform cannot be reached"
          description="On: a machine that has not reached this platform for a day may install a security or reliability fix from Mojo Up on its own. Off: it waits for you."
          control={<Toggle checked={p.policy.unattended !== 'forbidden'} isDisabled={busy} onChange={(on) => void setProduct({ unattended: on ? 'allowed' : 'forbidden' })} label={`Install urgent ${meta.title} fixes when this platform cannot be reached`} />}
        />
      ) : null}
      <ListRow
        title="Running now"
        description={p.running.length === 0 ? (p.product === 'mesh' ? 'No machine has reported its mesh agent yet.' : 'No machine of this kind has registered.') : `${total} ${total === 1 ? 'machine' : 'machines'} on ${p.running.length} ${p.running.length === 1 ? 'version' : 'versions'}.`}
      >
        {p.running.length ? (
          <ul className="grid max-w-xl gap-1.5">
            {p.running.map((r) => (
              <li key={r.version} className="text-sm">
                <div className="flex items-center justify-between gap-3">
                  <span className="mono">{r.version}</span>
                  <span className="text-muted tabular-nums">
                    {r.devices} {r.devices === 1 ? 'machine' : 'machines'}
                  </span>
                </div>
                <div className="mt-1 h-1 overflow-hidden rounded-full bg-surface-secondary" aria-hidden="true">
                  <div className="h-full rounded-full bg-accent" style={{ width: `${total ? Math.max(4, (r.devices / total) * 100) : 0}%` }} />
                </div>
              </li>
            ))}
          </ul>
        ) : null}
      </ListRow>
      {releases.length === 0 ? <ListBlock className="text-sm text-muted">No {[...channels].join(' or ')} releases are known yet.</ListBlock> : shown.map((r) => <ReleaseRow key={r.version} release={r} product={p} view={view} enterprise={enterprise} busy={busy} act={act} />)}
      {releases.length > 4 ? (
        <ListBlock className="py-1.5">
          <Button size="sm" variant="ghost" onPress={() => setShowAll((s) => !s)}>
            {showAll ? 'Show fewer' : `Show all ${releases.length}`}
          </Button>
        </ListBlock>
      ) : null}
    </ListGroup>
  );
}

/** One release, quietly: version, date, a Security mark when it applies, and which rings are offered it, in words. */
function ReleaseRow({ release: r, product: p, view, enterprise, busy, act }: { release: ReleaseManifest; product: ReleaseProductView; view: ReleasesOverview; enterprise: boolean; busy: boolean; act: (f: () => Promise<unknown>) => Promise<void> }) {
  const [open, setOpen] = useState(false);
  const offeredTo = view.rings.filter((ring) => p.offered[ring.id] === r.version).map((ring) => ring.name);
  const kept = p.mirrored.includes(r.version);
  const notes = r.releaseNotes || r.page ? <NotesLink release={r} open={open} onToggle={() => setOpen((o) => !o)} /> : null;
  return (
    <ListRow
      title={<span className="mono">{r.version}</span>}
      description={
        <>
          {day(r.publishedAt)}
          {r.yanked ? ` · Withdrawn by Mojo Up: ${r.yanked.reason}` : offeredTo.length ? ` · Offered to ${offeredTo.join(', ')}` : ''}
          {notes ? <> · {notes}</> : null}
        </>
      }
      status={r.yanked ? <StatusMark state="disabled" label="Withdrawn" /> : r.severity === 'security' ? <StatusMark state="high" label="Security" /> : r.severity === 'reliability' ? <StatusMark state="medium" label="Reliability" /> : undefined}
      compactControl
      control={
        enterprise && !r.yanked && hasInstallerStorage(view) ? (
          kept ? (
            <Tooltip delay={300}>
              <Tooltip.Trigger className="inline-flex size-8 items-center justify-center text-muted" aria-label="Served from this AIOE">
                <HardDrive className="size-4" aria-hidden="true" />
              </Tooltip.Trigger>
              <Tooltip.Content>Served from this AIOE</Tooltip.Content>
            </Tooltip>
          ) : (
            <Tooltip delay={300}>
              <Button size="sm" variant="ghost" isIconOnly aria-label={`Serve ${r.version} from this AIOE`} isDisabled={busy} onPress={() => void act(() => routes.mirrorRelease(p.product, r.version))}>
                <ArrowDownToLine className="size-4" />
              </Button>
              <Tooltip.Content>Serve this release from this AIOE, so machines download it here instead of from Mojo Up</Tooltip.Content>
            </Tooltip>
          )
        ) : undefined
      }
    >
      {open && r.releaseNotes ? <Notes text={r.releaseNotes} /> : null}
    </ListRow>
  );
}

/** This platform's own version, and the newest AIOE release Mojo Up lists. An AIOE never updates itself. */
function PlatformGroup({ view }: { view: ReleasesOverview }) {
  const meta = PRODUCT.aioe;
  const latest = view.platform.latest;
  const newer = latest && compareVersions(latest.version, view.platform.version) > 0;
  const [open, setOpen] = useState(false);
  return (
    <ListGroup title={meta.title} description={meta.what}>
      <ListRow title="Running here" description={isCloud() ? 'AI Cloud keeps itself up to date.' : 'Upgrades are applied by whoever runs this deployment, from the images and chart in the Mojo Up customer portal.'} value={<span className="mono text-foreground">{view.platform.version}</span>} />
      {latest ? (
        <ListRow
          title="Newest release"
          description={
            <>
              <span className="mono">{latest.version}</span>, published {day(latest.publishedAt)}
              {latest.releaseNotes || latest.page ? (
                <>
                  {' · '}
                  <NotesLink release={latest} open={open} onToggle={() => setOpen((o) => !o)} />
                </>
              ) : null}
              {latest.releaseNotes && latest.page ? (
                <>
                  {' · '}
                  <a className="text-accent hover:underline" href={latest.page} target="_blank" rel="noreferrer">
                    Downloads
                  </a>
                </>
              ) : null}
            </>
          }
          status={newer ? <StatusMark state="medium" label="Newer than this platform" /> : <StatusMark state="online" label="You are on it" />}
        >
          {open && latest.releaseNotes ? <Notes text={latest.releaseNotes} /> : null}
        </ListRow>
      ) : (
        <ListRow title="Newest release" description="No AIOE release has been listed upstream yet." />
      )}
    </ListGroup>
  );
}
